import { getHandshakeToken } from './postmessage';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export interface Objective {
  id: number;
  text: string;
  secondOrder: Array<{ id: number; text: string }>;
}

export interface ClassificationResult {
  needs_followup?: boolean;
  followup_question?: string | null;
  type: 'Bug' | 'Feature' | 'Chore';
  primary_objective_alignment: string;
  objective_number: number;
  severity: 'P1' | 'P2' | 'P3';
  urgency_driver: string;
  suggested_title?: string;
  clean_description?: string;
  reasoning: string;
}

export interface QaReviewResult {
  questions: string[];
}

export interface SubmitPayload {
  submission: Record<string, unknown>;
  objectives: Objective[];
}

export interface SubmitResult {
  id: number;
  app_url: string;
  name: string;
  reasoning: string;
}

class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API ${status}: ${body.slice(0, 200)}`);
  }
}

async function post<T>(path: string, payload: unknown): Promise<T> {
  if (!API_BASE) {
    throw new Error('VITE_API_BASE is not configured.');
  }
  const token = getHandshakeToken();
  if (!token) {
    throw new Error('Handshake token not received yet. Refresh the page or try again in a moment.');
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-OOP-Token': token,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new ApiError(res.status, await res.text());
  }
  return res.json() as Promise<T>;
}

async function get<T>(path: string): Promise<T> {
  if (!API_BASE) {
    throw new Error('VITE_API_BASE is not configured.');
  }
  const token = getHandshakeToken();
  if (!token) {
    throw new Error('Handshake token not received yet.');
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: { 'X-OOP-Token': token },
  });

  if (!res.ok) {
    throw new ApiError(res.status, await res.text());
  }
  return res.json() as Promise<T>;
}

export interface MeResult {
  found: boolean;
  mention_name: string | null;
  search_url: string | null;
}

export const api = {
  classify: (body: {
    description: string;
    objectives: Objective[];
    companyContext: string;
    severityPrompt: string;
  }) => post<ClassificationResult>('/api/classify', body),

  classifyFollowup: (body: {
    description: string;
    followupQuestion: string;
    followupAnswer: string;
    objectives: Objective[];
    companyContext: string;
    severityPrompt: string;
  }) => post<ClassificationResult>('/api/classify-followup', body),

  qaReview: (body: { formSummary: string }) => post<QaReviewResult>('/api/qa-review', body),

  submit: (body: SubmitPayload) => post<SubmitResult>('/api/submit', body),

  // GET — returns the submitter's Shortcut mention_name and a pre-built
  // search URL for their open tickets. Used by the SPA to render a
  // "View my open tickets" link in the page header.
  me: () => get<MeResult>('/api/me'),
};

export { ApiError };
